import { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import { supabase } from '../../lib/supabase';
import { useSucursal } from '../../utils/sucursalContext';
import AdminLayout from '../../components/layout/AdminLayout';

type Periodo = 'Hoy' | 'Semana' | 'Mes' | 'Trimestre' | 'Año';
type TabKey = 'general' | 'usuarios' | 'rutinas' | 'financiero';

type Metricas = {
  ingresos: number;
  usuariosActivos: number;
  asistencias: number;
  reservas: number;
  tasaRetencionPct: number; // 0..100
  nuevosUsuarios: number;
};

type PlanDist = { plan: string; cantidad: number };
type MetodoPago = { metodo: string; cantidad: number; total: number };
type BarRow = { label: string; value: number; display?: string };

const PERIODOS: Periodo[] = ['Hoy', 'Semana', 'Mes', 'Trimestre', 'Año'];

const TABS: Array<{ key: TabKey; label: string; icon: string }> = [
  { key: 'general', label: 'General', icon: 'ri-bar-chart-box-line' },
  { key: 'usuarios', label: 'Usuarios', icon: 'ri-user-line' },
  { key: 'rutinas', label: 'Rutinas', icon: 'ri-heart-pulse-line' },
  { key: 'financiero', label: 'Financiero', icon: 'ri-money-dollar-circle-line' },
];

const toDateOnlyLocal = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const getRango = (periodo: Periodo) => {
  const now = new Date();
  const start = new Date(now);

  if (periodo === 'Hoy') {
    start.setHours(0, 0, 0, 0);
  } else if (periodo === 'Semana') {
    start.setDate(now.getDate() - 6); // incluye hoy (7 días)
  } else if (periodo === 'Mes') {
    start.setMonth(now.getMonth() - 1);
  } else if (periodo === 'Trimestre') {
    start.setMonth(now.getMonth() - 3);
  } else {
    start.setFullYear(now.getFullYear() - 1);
  }

  return {
    fromDateOnly: toDateOnlyLocal(start),
    toDateOnly: toDateOnlyLocal(now),
  };
};

const getAgeYears = (fechaNacimiento: string | null | undefined) => {
  if (!fechaNacimiento) return null;
  const iso = String(fechaNacimiento).slice(0, 10);
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  if (age < 0 || age > 120) return null;
  return age;
};

export default function ReportesPage() {
  const { sucursalActiva } = useSucursal();

  const [tab, setTab] = useState<TabKey>('general');
  const [periodo, setPeriodo] = useState<Periodo>('Mes');
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [metricas, setMetricas] = useState<Metricas>({
    ingresos: 0,
    usuariosActivos: 0,
    asistencias: 0,
    reservas: 0,
    tasaRetencionPct: 0,
    nuevosUsuarios: 0,
  });

  const [planes, setPlanes] = useState<PlanDist[]>([]);
  const [metodosPago, setMetodosPago] = useState<MetodoPago[]>([]);
  const [clasesPopulares, setClasesPopulares] = useState<BarRow[]>([]);
  const [horariosPopulares, setHorariosPopulares] = useState<BarRow[]>([]);
  const [ingresosMensuales, setIngresosMensuales] = useState<BarRow[]>([]);
  const [asistenciasPorDia, setAsistenciasPorDia] = useState<BarRow[]>([]);
  const [topUsuarios, setTopUsuarios] = useState<Array<{ usuario: string; email: string; total: number }>>([]);
  const [ingresosPorPlan, setIngresosPorPlan] = useState<BarRow[]>([]);
  const [rutinasPopulares, setRutinasPopulares] = useState<Array<{ label: string; total: number; extra?: string }>>([]);
  const [edadesDist, setEdadesDist] = useState<BarRow[]>([]);
  const [usuariosNuevosMensuales, setUsuariosNuevosMensuales] = useState<BarRow[]>([]);

  const rango = useMemo(() => getRango(periodo), [periodo]);

  useEffect(() => {
    void cargarReportes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursalActiva?.id, periodo]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const cargarReportes = async () => {
    if (!sucursalActiva?.id) {
      setLoading(false);
      setErrorMsg(null);
      setMetricas({ ingresos: 0, usuariosActivos: 0, asistencias: 0, reservas: 0, tasaRetencionPct: 0, nuevosUsuarios: 0 });
      setPlanes([]);
      setMetodosPago([]);
      setClasesPopulares([]);
      setHorariosPopulares([]);
      setIngresosMensuales([]);
      setAsistenciasPorDia([]);
      setTopUsuarios([]);
      setIngresosPorPlan([]);
      setRutinasPopulares([]);
      setEdadesDist([]);
      setUsuariosNuevosMensuales([]);
      return;
    }

    try {
      setLoading(true);
      setErrorMsg(null);

      const [
        { data: usuarios, error: usuariosError },
        { data: pagos, error: pagosError },
        { data: horarios, error: horariosError },
      ] = await Promise.all([
        supabase
          .from('usuarios')
          .select('id, nombre, email, estado, tipo_membresia, created_at, fecha_nacimiento')
          .eq('sucursal_id', sucursalActiva.id),
        supabase
          .from('pagos')
          .select('id, usuario_id, monto, estado, metodo_pago, fecha_pago')
          .eq('sucursal_id', sucursalActiva.id)
          .gte('fecha_pago', rango.fromDateOnly)
          .lte('fecha_pago', rango.toDateOnly),
        supabase.from('horarios').select('id, nombre_clase, hora_inicio, hora_fin, capacidad_maxima').eq('sucursal_id', sucursalActiva.id),
      ]);

      if (usuariosError) throw usuariosError;
      if (pagosError) throw pagosError;
      if (horariosError) throw horariosError;

      const usuariosArr = usuarios || [];
      const pagosArr = pagos || [];
      const horariosArr = horarios || [];

      const usuarioById = new Map<string, any>(usuariosArr.map((u: any) => [u.id, u]));
      const horariosById = new Map<string, any>(horariosArr.map((h: any) => [h.id, h]));
      const horarioIds = horariosArr.map((h: any) => h.id);

      let reservasRaw: any[] = [];
      if (horarioIds.length) {
        const { data: reservasData, error: reservasError } = await supabase
          .from('reservas')
          .select('id, usuario_id, horario_id, fecha_reserva, estado')
          .gte('fecha_reserva', rango.fromDateOnly)
          .lte('fecha_reserva', rango.toDateOnly)
          .in('horario_id', horarioIds);
        if (reservasError) throw reservasError;
        reservasRaw = reservasData || [];
      }

      const pagosCompletados = pagosArr.filter((p: any) => p.estado === 'completado');
      const ingresos = pagosCompletados.reduce((sum: number, p: any) => sum + Number(p.monto || 0), 0);
      const usuariosActivos = usuariosArr.filter((u: any) => u.estado === 'activo').length;
      const reservas = reservasRaw.length;
      const asistencias = reservasRaw.filter((r: any) => r.estado === 'confirmada').length;

      const usuarioIdsActivos = new Set(usuariosArr.filter((u: any) => u.estado === 'activo').map((u: any) => u.id));
      const usuarioIdsConAsistencia = new Set(reservasRaw.filter((r: any) => r.estado === 'confirmada').map((r: any) => r.usuario_id));
      const activosConAsistencia = Array.from(usuarioIdsConAsistencia).filter((id) => usuarioIdsActivos.has(id)).length;
      const tasaRetencionPct = usuariosActivos > 0 ? Math.round((activosConAsistencia / usuariosActivos) * 100) : 0;

      const nuevosUsuarios = usuariosArr.filter((u: any) => {
        const created = String(u.created_at || '');
        const dateOnly = created ? created.slice(0, 10) : '';
        return dateOnly >= rango.fromDateOnly && dateOnly <= rango.toDateOnly;
      }).length;

      setMetricas({ ingresos, usuariosActivos, asistencias, reservas, tasaRetencionPct, nuevosUsuarios });

      const planMap = new Map<string, number>();
      usuariosArr.forEach((u: any) => {
        const plan = u.tipo_membresia?.trim() || 'Sin plan';
        planMap.set(plan, (planMap.get(plan) || 0) + 1);
      });
      setPlanes(
        Array.from(planMap.entries())
          .map(([plan, cantidad]) => ({ plan, cantidad }))
          .sort((a, b) => b.cantidad - a.cantidad)
      );

      const metodoMap = new Map<string, MetodoPago>();
      pagosArr.forEach((p: any) => {
        const key = p.metodo_pago || 'sin_definir';
        const current = metodoMap.get(key) || { metodo: key, cantidad: 0, total: 0 };
        current.cantidad += 1;
        current.total += Number(p.monto || 0);
        metodoMap.set(key, current);
      });
      setMetodosPago(Array.from(metodoMap.values()).sort((a, b) => b.total - a.total));

      const clasesMap = new Map<string, number>();
      reservasRaw.forEach((r: any) => {
        const h = horariosById.get(r.horario_id);
        const nombre = (h?.nombre_clase || 'Clase') as string;
        clasesMap.set(nombre, (clasesMap.get(nombre) || 0) + 1);
      });
      setClasesPopulares(
        Array.from(clasesMap.entries())
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8)
      );

      const horariosMap = new Map<string, number>();
      reservasRaw.forEach((r: any) => {
        const h = horariosById.get(r.horario_id);
        const bloque = h?.hora_inicio && h?.hora_fin ? `${String(h.hora_inicio).slice(0, 5)} - ${String(h.hora_fin).slice(0, 5)}` : 'Horario';
        horariosMap.set(bloque, (horariosMap.get(bloque) || 0) + 1);
      });
      setHorariosPopulares(
        Array.from(horariosMap.entries())
          .map(([label, value]) => ({ label, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 8)
      );

      const ingresosPorYM = new Map<string, number>();
      pagosCompletados.forEach((p: any) => {
        const ym = String(p.fecha_pago || '').slice(0, 7); // YYYY-MM
        if (!ym) return;
        ingresosPorYM.set(ym, (ingresosPorYM.get(ym) || 0) + Number(p.monto || 0));
      });
      const ymSorted = Array.from(ingresosPorYM.entries()).sort(([a], [b]) => (a < b ? -1 : 1));
      setIngresosMensuales(
        ymSorted.slice(-6).map(([ym, value]) => ({
          label: capitalize(new Date(`${ym}-01T00:00:00`).toLocaleDateString('es-CL', { month: 'long' })),
          value,
          display: formatCurrency(value),
        }))
      );

      const dayOrder: Array<{ key: number; label: string }> = [
        { key: 1, label: 'Lun' },
        { key: 2, label: 'Mar' },
        { key: 3, label: 'Mié' },
        { key: 4, label: 'Jue' },
        { key: 5, label: 'Vie' },
        { key: 6, label: 'Sáb' },
        { key: 0, label: 'Dom' },
      ];
      const asistenciasByDow = new Map<number, number>(dayOrder.map((d) => [d.key, 0]));
      reservasRaw
        .filter((r: any) => r.estado === 'confirmada')
        .forEach((r: any) => {
          const d = new Date(`${String(r.fecha_reserva).slice(0, 10)}T00:00:00`);
          const dow = d.getDay();
          asistenciasByDow.set(dow, (asistenciasByDow.get(dow) || 0) + 1);
        });
      setAsistenciasPorDia(dayOrder.map((d) => ({ label: d.label, value: asistenciasByDow.get(d.key) || 0 })));

      const topMap = new Map<string, number>();
      reservasRaw
        .filter((r: any) => r.estado === 'confirmada')
        .forEach((r: any) => topMap.set(r.usuario_id, (topMap.get(r.usuario_id) || 0) + 1));
      setTopUsuarios(
        Array.from(topMap.entries())
          .map(([id, total]) => {
            const u = usuarioById.get(id);
            return { usuario: u?.nombre || 'Usuario', email: u?.email || '', total };
          })
          .sort((a, b) => b.total - a.total)
          .slice(0, 5)
      );

      const ingresoPlanMap = new Map<string, number>();
      pagosCompletados.forEach((p: any) => {
        const u = usuarioById.get(p.usuario_id);
        const plan = u?.tipo_membresia?.trim() || 'Sin plan';
        ingresoPlanMap.set(plan, (ingresoPlanMap.get(plan) || 0) + Number(p.monto || 0));
      });
      setIngresosPorPlan(
        Array.from(ingresoPlanMap.entries())
          .map(([label, value]) => ({ label, value, display: formatCurrency(value) }))
          .sort((a, b) => b.value - a.value)
      );

      const ageBuckets = [
        { key: '18-25', label: '18-25 años', min: 18, max: 25 },
        { key: '26-35', label: '26-35 años', min: 26, max: 35 },
        { key: '36-45', label: '36-45 años', min: 36, max: 45 },
        { key: '46-55', label: '46-55 años', min: 46, max: 55 },
        { key: '56+', label: '56+ años', min: 56, max: 200 },
      ];
      const ageCounts = new Map<string, number>(ageBuckets.map((b) => [b.key, 0]));
      let sinDato = 0;
      usuariosArr.forEach((u: any) => {
        const age = getAgeYears(u.fecha_nacimiento);
        if (age == null) {
          sinDato += 1;
          return;
        }
        const bucket = ageBuckets.find((b) => age >= b.min && age <= b.max);
        if (!bucket) {
          sinDato += 1;
          return;
        }
        ageCounts.set(bucket.key, (ageCounts.get(bucket.key) || 0) + 1);
      });
      const edadesRows: BarRow[] = ageBuckets.map((b) => ({ label: b.label, value: ageCounts.get(b.key) || 0 }));
      if (sinDato) edadesRows.push({ label: 'Sin dato', value: sinDato });
      setEdadesDist(edadesRows.filter((r) => r.value > 0));

      const nuevosPorYM = new Map<string, number>();
      usuariosArr.forEach((u: any) => {
        const ym = String(u.created_at || '').slice(0, 7);
        if (!ym) return;
        nuevosPorYM.set(ym, (nuevosPorYM.get(ym) || 0) + 1);
      });
      const nuevosYmSorted = Array.from(nuevosPorYM.entries()).sort(([a], [b]) => (a < b ? -1 : 1));
      setUsuariosNuevosMensuales(
        nuevosYmSorted.slice(-6).map(([ym, value]) => ({
          label: capitalize(new Date(`${ym}-01T00:00:00`).toLocaleDateString('es-CL', { month: 'short' })),
          value,
        }))
      );

      // Rutinas populares: mejor esfuerzo. Si tablas no existen o no hay permisos, se muestra vacío.
      const { data: entrenamientosData, error: entrenamientosError } = await supabase
        .from('entrenamientos')
        .select('rutina_id')
        .eq('sucursal_id', sucursalActiva.id)
        .gte('fecha', rango.fromDateOnly)
        .lte('fecha', rango.toDateOnly);
      const entrenamientosArr: any[] = entrenamientosError ? [] : entrenamientosData || [];

      const { data: rutinasData, error: rutinasError } = await supabase.from('rutinas').select('id, nombre, usuarios_asignados');
      const rutinasArr: any[] = rutinasError ? [] : rutinasData || [];

      const rutinasById = new Map<string, any>(rutinasArr.map((r: any) => [r.id, r]));
      const popRutinasMap = new Map<string, number>();

      if (entrenamientosArr.length) {
        entrenamientosArr.forEach((e: any) => {
          if (!e.rutina_id) return;
          const rid = String(e.rutina_id);
          popRutinasMap.set(rid, (popRutinasMap.get(rid) || 0) + 1);
        });

        setRutinasPopulares(
          Array.from(popRutinasMap.entries())
            .map(([rid, total]) => ({ label: rutinasById.get(rid)?.nombre || 'Rutina', total, extra: 'sesiones registradas' }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 6)
        );
      } else {
        const idsSucursal = new Set(usuariosArr.map((u: any) => String(u.id)));
        rutinasArr.forEach((r: any) => {
          const assigned: string[] = Array.isArray(r.usuarios_asignados) ? r.usuarios_asignados : [];
          const countInSucursal = assigned.filter((id) => idsSucursal.has(String(id))).length;
          if (countInSucursal > 0) popRutinasMap.set(String(r.id), countInSucursal);
        });

        setRutinasPopulares(
          Array.from(popRutinasMap.entries())
            .map(([rid, total]) => ({ label: rutinasById.get(rid)?.nombre || 'Rutina', total, extra: 'usuarios asignados' }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 6)
        );
      }
    } catch (error) {
      console.error('Error cargando reportes:', error);
      setErrorMsg('No se pudieron cargar los reportes. Revisa tu conexión y permisos.');
      setMetricas({ ingresos: 0, usuariosActivos: 0, asistencias: 0, reservas: 0, tasaRetencionPct: 0, nuevosUsuarios: 0 });
      setPlanes([]);
      setMetodosPago([]);
      setClasesPopulares([]);
      setHorariosPopulares([]);
      setIngresosMensuales([]);
      setAsistenciasPorDia([]);
      setTopUsuarios([]);
      setIngresosPorPlan([]);
      setRutinasPopulares([]);
      setEdadesDist([]);
      setUsuariosNuevosMensuales([]);
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(13, 148, 136);
    doc.rect(0, 0, pageWidth, 26, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Reportes y Estadísticas', 14, 16);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Sucursal: ${sucursalActiva?.nombre || '—'}  |  Período: ${periodo} (${rango.fromDateOnly} a ${rango.toDateOnly})`, 14, 23);

    let y = 36;

    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(`Sección: ${TABS.find((t) => t.key === tab)?.label || 'General'}`, 14, y);
    y += 10;

    const addLine = (label: string, value: string) => {
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`${label}:`, 14, y);
      doc.setTextColor(17, 24, 39);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 62, y);
      y += 7;
    };

    addLine('Ingresos', formatCurrency(metricas.ingresos));
    addLine('Usuarios activos', String(metricas.usuariosActivos));
    addLine('Asistencias', String(metricas.asistencias));
    addLine('Reservas', String(metricas.reservas));
    addLine('Tasa de retención', `${metricas.tasaRetencionPct}%`);
    addLine('Nuevos usuarios', String(metricas.nuevosUsuarios));

    y += 5;

    const addList = (title: string, rows: Array<{ label: string; value: number }>, maxRows = 8) => {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(17, 24, 39);
      doc.text(title, 14, y);
      y += 7;

      if (!rows.length) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text('Sin datos', 14, y);
        y += 7;
        return;
      }

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(17, 24, 39);
      rows.slice(0, maxRows).forEach((r) => {
        doc.text(`${r.label}: ${r.value}`, 14, y);
        y += 6;
      });
    };

    if (tab === 'general') {
      addList('Ingresos mensuales', ingresosMensuales.map((r) => ({ label: r.label, value: r.value })), 6);
      y += 2;
      addList('Asistencias por día', asistenciasPorDia.map((r) => ({ label: r.label, value: r.value })), 7);
    } else if (tab === 'usuarios') {
      addList('Top usuarios (asistencias)', topUsuarios.map((u) => ({ label: u.usuario, value: u.total })), 5);
    } else if (tab === 'rutinas') {
      addList('Rutinas populares', rutinasPopulares.map((r) => ({ label: r.label, value: r.total })), 6);
    } else if (tab === 'financiero') {
      addList('Ingresos por plan', ingresosPorPlan.map((p) => ({ label: p.label, value: p.value })), 8);
    }

    const filename = `reportes_${(sucursalActiva?.nombre || 'sucursal')
      .toLowerCase()
      .replace(/\\s+/g, '_')}_${rango.fromDateOnly}_${rango.toDateOnly}.pdf`;
    doc.save(filename);
  };

  return (
    <AdminLayout>
      <div className="p-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reportes y Estadísticas</h1>
            <p className="text-gray-600 mt-1">Análisis completo del rendimiento del gimnasio</p>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value as Periodo)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              {PERIODOS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <button
              onClick={exportarPDF}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line"></i>
              Exportar PDF
            </button>
          </div>
        </div>

        {errorMsg ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{errorMsg}</div>
        ) : null}

        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-100 p-2 w-fit">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                tab === t.key ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className={t.icon}></i>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-96">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <KpiCard title="Ingresos Totales" value={formatCurrency(metricas.ingresos)} sub="vs período seleccionado" icon="ri-money-dollar-circle-line" />
              <KpiCard title="Usuarios Activos" value={String(metricas.usuariosActivos)} sub="usuarios activos en la sucursal" icon="ri-user-line" />
              <KpiCard title="Asistencias" value={String(metricas.asistencias)} sub="reservas confirmadas" icon="ri-calendar-check-line" />
              <KpiCard title="Reservas" value={String(metricas.reservas)} sub="todas las reservas del período" icon="ri-bookmark-line" />
              <KpiCard title="Tasa de Retención" value={`${metricas.tasaRetencionPct}%`} sub="activos con asistencia" icon="ri-heart-line" />
              <KpiCard title="Nuevos Usuarios" value={String(metricas.nuevosUsuarios)} sub="altas en el período" icon="ri-user-add-line" />
            </div>

            {tab === 'general' ? (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Panel title="Ingresos Mensuales">
                    <BarList rows={ingresosMensuales} emptyText="Sin ingresos en el período" colorClass="bg-teal-600" />
                  </Panel>

                  <Panel title="Asistencias por Día">
                    <BarList rows={asistenciasPorDia} emptyText="Sin asistencias en el período" colorClass="bg-blue-600" />
                  </Panel>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Panel title="Distribución de Planes">
                    <BarList
                      rows={planes.map((p) => ({ label: p.plan, value: p.cantidad, display: String(p.cantidad) }))}
                      emptyText="Sin datos"
                      colorClass="bg-purple-600"
                    />
                  </Panel>

                  <Panel title="Horarios Más Populares">
                    <BarList rows={horariosPopulares} emptyText="Sin datos" colorClass="bg-orange-600" />
                  </Panel>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Panel title="Clases Más Reservadas">
                    <BarList rows={clasesPopulares} emptyText="Sin datos" colorClass="bg-amber-600" />
                  </Panel>

                  <Panel title="Métodos de Pago">
                    {metodosPago.length ? (
                      <div className="space-y-3">
                        {metodosPago.map((m) => (
                          <div key={m.metodo} className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900 capitalize">{m.metodo}</p>
                              <p className="text-xs text-gray-500">{m.cantidad} transacciones</p>
                            </div>
                            <div className="text-sm font-semibold text-gray-900">{formatCurrency(m.total)}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Sin datos</p>
                    )}
                  </Panel>

                  <Panel title="Top Usuarios del Período">
                    {topUsuarios.length ? (
                      <div className="divide-y divide-gray-100">
                        {topUsuarios.map((u) => (
                          <div key={`${u.usuario}-${u.email}`} className="py-3 flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{u.usuario}</p>
                              <p className="text-xs text-gray-500">{u.email}</p>
                            </div>
                            <div className="text-sm font-semibold text-gray-900">{u.total}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Sin datos</p>
                    )}
                  </Panel>
                </div>
              </>
            ) : null}

            {tab === 'usuarios' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel title="Distribución por Género">
                  <EmptyState
                    title="No disponible"
                    description="La tabla de usuarios no tiene un campo de género. Si lo necesitas, lo agregamos y se completa desde el portal o la app."
                  />
                </Panel>

                <Panel title="Distribución por Edad">
                  <BarList rows={edadesDist} emptyText="Sin datos suficientes (fecha de nacimiento vacía)" colorClass="bg-emerald-600" />
                  <p className="text-xs text-gray-500 mt-3">
                    Esta sección usa <span className="font-medium">fecha de nacimiento</span> del usuario.
                  </p>
                </Panel>

                <Panel title="Nuevos Usuarios por Mes" className="lg:col-span-2">
                  <BarList rows={usuariosNuevosMensuales} emptyText="Sin datos" colorClass="bg-green-600" />
                </Panel>

                <Panel title="Top Usuarios del Período" className="lg:col-span-2">
                  {topUsuarios.length ? (
                    <div className="divide-y divide-gray-100">
                      {topUsuarios.map((u) => (
                        <div key={`${u.usuario}-${u.email}`} className="py-3 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{u.usuario}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                          <div className="text-sm font-semibold text-gray-900">{u.total}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Sin datos</p>
                  )}
                </Panel>
              </div>
            ) : null}

            {tab === 'rutinas' ? (
              <div className="grid grid-cols-1 gap-6">
                <Panel title="Rutinas Más Populares">
                  <BarList
                    rows={rutinasPopulares.map((r) => ({ label: r.label, value: r.total, display: `${r.total}${r.extra ? ` ${r.extra}` : ''}` }))}
                    emptyText="Sin datos"
                    colorClass="bg-purple-600"
                  />
                </Panel>
              </div>
            ) : null}

            {tab === 'financiero' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel title="Ingresos por Plan" className="lg:col-span-2">
                  <BarList rows={ingresosPorPlan} emptyText="Sin datos" colorClass="bg-teal-600" />
                </Panel>

                <Panel title="Métodos de Pago">
                  {metodosPago.length ? (
                    <div className="space-y-3">
                      {metodosPago.map((m) => (
                        <div key={m.metodo} className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">{m.metodo}</p>
                            <p className="text-xs text-gray-500">{m.cantidad} transacciones</p>
                          </div>
                          <div className="text-sm font-semibold text-gray-900">{formatCurrency(m.total)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">Sin datos</p>
                  )}
                </Panel>

              </div>
            ) : null}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function Panel({ title, children, className = '' }: { title: string; children: React.ReactNode; className?: string }) {
  return (
    <section className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 ${className}`}>
      <h2 className="text-base font-semibold text-gray-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function KpiCard({ title, value, sub, icon }: { title: string; value: string; sub: string; icon: string }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500 mt-2">{sub}</p>
        </div>
        <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
          <i className={`${icon} text-2xl text-teal-700`}></i>
        </div>
      </div>
    </div>
  );
}

function BarList({ rows, emptyText, colorClass }: { rows: BarRow[]; emptyText: string; colorClass: string }) {
  if (!rows.length) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }

  const max = Math.max(...rows.map((r) => r.value), 0);
  return (
    <div className="space-y-3">
      {rows.map((r) => {
        const pct = max > 0 ? Math.round((r.value / max) * 100) : 0;
        return (
          <div key={r.label} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-800">{r.label}</span>
              <span className="font-semibold text-gray-900">{r.display ?? String(r.value)}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-2 ${colorClass}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-600 mt-1">{description}</p>
    </div>
  );
}
